<template lang="pug">
.input-dialog-horizontal
  //- 入力方法選択
  .input-dialog-horizontal__input-type
    template(v-if='answerFormat.isTegaki')
      input.input-dialog-horizontal__input-type__input(
        type='radio',
        :id='getInputId(inputDialogType.TEGAKI)',
        :value='inputDialogType.TEGAKI',
        v-model='selectedInputType',
        @change='changeInputType'
      )
      label.input-dialog-horizontal__input-type__label(:for='getInputId(inputDialogType.TEGAKI)')
        fa-icon(icon='pen')
    template(v-if='answerFormat.isKeyboard')
      input.input-dialog-horizontal__input-type__input(
        type='radio',
        :id='getInputId(inputDialogType.KEYBOARD)',
        :value='inputDialogType.KEYBOARD',
        v-model='selectedInputType',
        @change='changeInputType'
      )
      label.input-dialog-horizontal__input-type__label(:for='getInputId(inputDialogType.KEYBOARD)')
        fa-icon(icon='keyboard')

  .input-dialog-horizontal__text-before(v-if='beforeText') {{ beforeText }}
  //- 手書き入力
  text-field-tegaki.input-dialog-horizontal__input(
    v-model='inputValue',
    :lang='lang',
    :savedStrokes='savedStrokes',
    :canStandardKeyboard='answerFormat.canStandardKeyboard',
    :isEnglish='answerFormat.isEnglishTegaki',
    @set-stroke-data='setStrokeData',
    v-if='useTegaki'
  )
    template(#resultTitle)
      slot(name='tegakiResultTitle')
    template(#recognizeButtonLabel)
      slot(name='tegakiRecognizeButtonLabel')
  //- 数式キーボード
  text-field-math.input-dialog-horizontal__input(
    v-model='inputValue',
    :keyboardName='mathKeyboardName',
    v-if='useMathKeyboard'
  )
  //- 化学式キーボード
  text-field-science.input-dialog-horizontal__input-science(v-model='inputValue', v-if='useScienceKeyboard')
  .input-dialog-horizontal__text-after(v-if='afterText') {{ afterText }}
</template>

<script lang="ts">
import { Component, Prop, Vue } from 'vue-property-decorator'
import TextFieldTegaki from '@/components/organisms/student/TextFieldTegaki.vue'
import TextFieldMath from '@/components/molecules/student/TextFieldMath.vue'
import TextFieldScience from '@/components/molecules/student/TextFieldScience.vue'
import { StrokePoint } from '@/models/StrokePoint'
import { LangType, InputDialogType } from '@/models/studyModel'
import { AnswerFormat } from '@/const/AnswerFormats'

/**
 * 手書き/ソフトウェアキーボード入力
 */
@Component({
  components: {
    TextFieldTegaki,
    TextFieldMath,
    TextFieldScience,
  },
})
export default class InputDialogHorizontal extends Vue {
  // 入力値
  @Prop()
  value!: string

  // 前方テキスト
  @Prop()
  beforeText?: string

  // 後方テキスト
  @Prop()
  afterText?: string

  // 使用入力方法
  @Prop()
  inputType!: InputDialogType

  // 入力形式
  @Prop()
  answerFormat!: AnswerFormat

  /**
   * 手書きダイアログ用
   */
  // MyScriptに渡す言語タイプ
  @Prop()
  lang?: LangType
  // 保存済みペンストローク
  @Prop()
  savedStrokes?: StrokePoint[][]

  /**
   * キーボード用
   */
  // 数学キーボード名
  @Prop()
  mathKeyboardName?: string

  // 入力値ゲッター・セッター
  get inputValue(): string {
    return this.value
  }
  set inputValue(value: string) {
    this.$emit('input', value)
  }

  // 入力方法ゲッターセッター
  get selectedInputType(): InputDialogType {
    return this.inputType
  }
  set selectedInputType(value: InputDialogType) {
    this.$emit('set-input-type', value)
  }

  // コンポーネント単位のユニークID取得
  private readonly UID = this.$uuid.v4()

  // inputタグの個別ID
  private getInputId(inputDialogType: InputDialogType): string {
    return `id${this.UID}_${inputDialogType}`
  }

  private get inputDialogType(): InputDialogType {
    return InputDialogType
  }

  // どの入力方式を使用するか
  private get useTegaki(): boolean {
    return this.selectedInputType === InputDialogType.TEGAKI
  }
  private get useMathKeyboard(): boolean {
    return this.selectedInputType === InputDialogType.KEYBOARD && !!this.answerFormat.isMathKeyboard
  }
  private get useScienceKeyboard(): boolean {
    return this.selectedInputType == InputDialogType.KEYBOARD && !!this.answerFormat.isScienceKeyboard
  }

  // ペンストローク情報をemit
  private setStrokeData(strokes: StrokePoint[][]) {
    this.$emit('set-stroke-data', strokes)
  }

  // 入力方法変更時は入力内容をクリアする
  private changeInputType() {
    this.$emit('input', '')
  }
}
</script>

<style lang="stylus" scoped>
.input-dialog-horizontal
  display flex
  align-items center
  flex-direction row
  flex-wrap wrap
  justify-content flex-start

  &__input-type
    margin-right 5px
    &__input
      opacity 0
      position absolute
    &__label
      display block
      margin 0
      padding 0 5px
      float left
      border 1px solid var(--color-study-input-dialog-button__border)
    &:after
      content ""
      display block
      clear left
    input[type="radio"]:checked
      & + label
        background-color var(--color-study-input-dialog-button-active__background)

  &__text-before
    margin-right 10px

  &__text-after
    margin-right 5px

  &__input
    width 10.5rem
    border-radius 4px
    margin-right 10px

  &__input-science
    width 12rem
    border-radius 4px
    margin-right 10px
</style>
